import { useEffect, useRef } from 'react';
import {
  Node,
  useNodesInitialized,
  useOnViewportChange,
  useReactFlow,
} from 'reactflow';
import { useActiveJourneyMapDraft } from '../../../../hooks/journey-map-hooks';
import { useAppDispatch } from '../../../../support/store';
import { updateJourneyMapDraftField } from '../../../../support/slices/journeyMapBuilderSlice';
import { MAX_ZOOM, MIN_ZOOM } from './journeyMapCanvasConstants';

type Props = {
  nodes: Node[];
};

export default function JourneyMapCanvasViewportSync({ nodes }: Props) {
  const reactFlow = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const hasFitInitiallyRef = useRef(false);
  const draft = useActiveJourneyMapDraft();
  const dispatch = useAppDispatch();

  const persistViewport = (viewport: { x: number; y: number; zoom: number }) => {
    const currentMetadata =
      draft?.metadata && typeof draft.metadata === 'object' ? draft.metadata : {};

    const nextViewport = {
      x: viewport.x,
      y: viewport.y,
      zoom: viewport.zoom,
    };

    const existingViewport = currentMetadata?.viewport || {};
    const hasChanged =
      Number(existingViewport?.x) !== Number(nextViewport.x) ||
      Number(existingViewport?.y) !== Number(nextViewport.y) ||
      Number(existingViewport?.zoom) !== Number(nextViewport.zoom);

    if (!hasChanged) return;

    dispatch(
      updateJourneyMapDraftField({
        field: 'metadata',
        value: {
          ...currentMetadata,
          viewport: nextViewport,
        },
      }),
    );
  };

  useEffect(() => {
    if (!nodesInitialized) return;
    if (hasFitInitiallyRef.current) return;
    if (!nodes.length) return;

    const savedViewport =
      draft?.metadata &&
      typeof draft.metadata === 'object' &&
      draft.metadata?.viewport
        ? draft.metadata.viewport
        : null;

    const hasSavedViewport =
      savedViewport &&
      Number.isFinite(Number(savedViewport?.x)) &&
      Number.isFinite(Number(savedViewport?.y)) &&
      Number.isFinite(Number(savedViewport?.zoom));

    if (hasSavedViewport) {
      reactFlow.setViewport(
        {
          x: Number(savedViewport.x),
          y: Number(savedViewport.y),
          zoom: Number(savedViewport.zoom),
        },
        { duration: 0 },
      );

      hasFitInitiallyRef.current = true;
      return;
    }

    reactFlow.fitView({
      padding: 0.12,
      duration: 300,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
    });

    requestAnimationFrame(() => {
      const viewport = reactFlow.getViewport();
      persistViewport(viewport);
    });

    hasFitInitiallyRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesInitialized, nodes, reactFlow, draft]);

  useOnViewportChange({
    onEnd: (viewport) => {
      persistViewport(viewport);
    },
  });

  return null;
}